/**
 * Approvals — the Action queue. Approve / edit-then-approve / reject with a
 * one-tap reason category. The decide call proxies to the orchestrator; the
 * dashboard-api enriches edit_diff + decision_reason for Brand Memory.
 */
import { useRef, useState } from "react";
import { Chip, Empty, ErrorNote, Panel, useApi } from "../components/ui.js";
import { api, ApiError } from "../lib/api.js";
import { fmtDate, fmtDateTime } from "../lib/format.js";
import type { ApprovalRow } from "../lib/types.js";

const REASONS = ["off_brand", "inaccurate", "wrong_audience", "bad_timing", "too_aggressive", "other"];
const TABS = ["pending", "approved", "rejected", "edited", "expired"] as const;

interface ProposedChange { field: string; stepNumber: number; before: string; after: string }

export function Approvals() {
  const [tab, setTab] = useState<(typeof TABS)[number]>("pending");
  const list = useApi<{ approvals: ApprovalRow[] }>(`/api/approvals?status=${tab}&limit=50`);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({}); // `${id}:${idx}` → edited after-text
  const [reason, setReason] = useState(REASONS[0]);
  const [feedback, setFeedback] = useState<Record<string, string>>({}); // `${id}` → regenerate feedback
  const fileRef = useRef<HTMLInputElement>(null);

  const open = list.data?.approvals.find((a) => a.id === openId) ?? null;
  const changes: ProposedChange[] = (open?.payload?.["proposedChanges"] as ProposedChange[] | undefined) ?? [];
  const replyDraft = open?.payload?.["kind"] === "reply_draft"
    ? (open.payload as unknown as {
        contactEmail: string; contactName?: string | null; intent: string; confidence?: number;
        summary?: string; draftSubject: string; draftBodyText: string; calendarIncluded?: boolean;
      })
    : null;
  const contentPost = open?.payload?.["kind"] === "content_post"
    ? (open.payload as unknown as {
        title: string; assetType: string; channel: string; personaVoice?: string; body: string;
        committeeScore?: number; committeeOutcome?: string;
        committeeFlags?: string[]; committeeChanges?: string[];
        escalationReason?: string; qaFlags?: string[];
        revisedByCommittee?: boolean; originalBody?: string;
        revisedByFounder?: boolean; founderFeedback?: string[];
        mediaUrl?: string; mediaId?: string;
        productionCostUsd?: number; productionModel?: string;
      })
    : null;
  const stateFlag = open?.payload?.["kind"] === "state_flag"
    ? (open.payload as unknown as {
        key: string; value: boolean; description?: string;
        preview?: { title?: string; what?: string; subject?: string; body?: string };
      })
    : null;
  const leadBatchId = open?.payload?.["kind"] === "lead_batch" ? String(open.payload["leadBatchId"] ?? "") : null;
  const leadBatch = useApi<{
    batch: { cohort_key: string; run_date: string; sourced_count: unknown; kept_count: unknown };
    leads: Array<{ id: string; email: string; full_name: string | null; title: string | null; company: string | null; industry: string | null; location: string | null; needs_review: boolean | null; dedupe_status: string | null }>;
  }>(leadBatchId && /^\d+$/.test(leadBatchId) ? `/api/leads/batches/${leadBatchId}` : null);

  async function decide(a: ApprovalRow, decision: "approved" | "rejected" | "edited") {
    setBusy(true);
    setNotice(null);
    try {
      let editedPayload: unknown;
      if (decision === "edited" && contentPost) {
        editedPayload = {
          ...a.payload,
          body: edits[`${a.id}:body`] ?? contentPost.body,
        };
      } else if (decision === "edited" && replyDraft) {
        editedPayload = {
          ...a.payload,
          draftSubject: edits[`${a.id}:subject`] ?? replyDraft.draftSubject,
          draftBodyText: edits[`${a.id}:body`] ?? replyDraft.draftBodyText,
        };
      } else if (decision === "edited") {
        const newChanges = changes.map((c, i) => ({ ...c, after: edits[`${a.id}:${i}`] ?? c.after }));
        editedPayload = { ...a.payload, proposedChanges: newChanges };
      }
      await api.post(`/api/approvals/${a.id}/decide`, {
        decision: decision === "edited" ? "approved" : decision,
        ...(editedPayload !== undefined ? { editedPayload } : {}),
        ...(decision === "rejected" ? { decisionReason: reason } : {}),
      });
      setNotice(`#${a.id} ${decision}`);
      setOpenId(null);
      list.reload();
    } catch (e) {
      setNotice(e instanceof ApiError && e.status === 409 ? `#${a.id} was already decided (409) — refreshed` : `failed: ${e instanceof Error ? e.message : e}`);
      if (e instanceof ApiError && e.status === 409) list.reload();
    } finally {
      setBusy(false);
    }
  }

  // Founder feedback → the system regenerates the content; the approval stays
  // pending so you review the new draft (rule #6: nothing auto-publishes).
  async function regenerate(a: ApprovalRow) {
    const note = (feedback[a.id] ?? "").trim();
    if (!note) return;
    setBusy(true);
    setNotice(null);
    try {
      const res = await api.post<{ ok: boolean; changed: boolean }>(`/api/approvals/${a.id}/regenerate`, { feedback: note });
      setFeedback({ ...feedback, [a.id]: "" });
      setEdits((e) => { const n = { ...e }; delete n[`${a.id}:body`]; return n; }); // drop any stale edit
      setNotice(res.changed
        ? `#${a.id} regenerated — review the new draft`
        : `#${a.id}: that feedback couldn't be applied without exceeding what the app does — draft left unchanged`);
      list.reload();
    } catch (e) {
      setNotice(`regenerate failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusy(false);
    }
  }

  // Re-roll the AI image (the hybrid control). Stays pending; reload shows it.
  async function regenerateImage(a: ApprovalRow) {
    setBusy(true);
    setNotice(null);
    try {
      await api.post(`/api/approvals/${a.id}/regenerate-image`, {});
      setNotice(`#${a.id}: image regenerated`);
      list.reload();
    } catch (e) {
      setNotice(`image regenerate failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusy(false);
    }
  }

  // Upload your own image — the other half of the hybrid control. Supersedes the
  // AI draft; the approval stays pending so the new image shows on reload.
  async function uploadImage(a: ApprovalRow, file: File) {
    setBusy(true);
    setNotice(null);
    try {
      const form = new FormData();
      form.append("image", file);
      await api.upload(`/api/approvals/${a.id}/replace-image`, form);
      setNotice(`#${a.id}: image replaced with your upload`);
      list.reload();
    } catch (e) {
      setNotice(`upload failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Approvals</h1>
        <span className="stamp">action queue · rule #6: nothing auto-applies</span>
      </div>

      <div className="row" style={{ marginBottom: 14 }}>
        {TABS.map((t) => (
          <button key={t} className={`btn sm ${tab === t ? "primary" : ""}`} onClick={() => { setTab(t); setOpenId(null); }}>{t}</button>
        ))}
        {notice ? <span className="tag" style={{ color: "var(--green)" }}>{notice}</span> : null}
      </div>

      {list.error ? <ErrorNote error={list.error} /> : null}

      <div className="grid cols-2">
        <Panel title={`${tab} requests`}>
          {list.data && list.data.approvals.length === 0 ? (
            <Empty>queue is empty</Empty>
          ) : (
            <table className="data">
              <thead><tr><th>#</th><th>kind</th><th>type</th><th>created</th><th>status</th></tr></thead>
              <tbody>
                {(list.data?.approvals ?? []).map((a) => (
                  <tr key={a.id} className="clickable" onClick={() => setOpenId(a.id)}
                      style={openId === a.id ? { outline: "1px solid var(--red-dim)" } : undefined}>
                    <td className="num">{a.id}</td>
                    <td><Chip tone={a.status === "pending" ? "amber" : undefined}>{a.kind}</Chip></td>
                    <td className="dim">{a.action_type ?? "—"}</td>
                    <td className="num dim">{fmtDate(a.created_at)}</td>
                    <td className="dim">{a.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title={open ? `request #${open.id}` : "detail"}>
          {!open ? (
            <Empty>select a request</Empty>
          ) : (
            <>
              {leadBatchId ? (
                leadBatch.data ? (
                  <>
                    <div className="tag" style={{ marginBottom: 8 }}>
                      lead batch · {leadBatch.data.batch.cohort_key} · {fmtDate(String(leadBatch.data.batch.run_date))} ·
                      {" "}{leadBatch.data.leads.length} staged
                    </div>
                    <div style={{ maxHeight: 340, overflow: "auto" }}>
                      <table className="data">
                        <thead><tr><th>name</th><th>title</th><th>company</th><th>location</th><th>flags</th></tr></thead>
                        <tbody>
                          {leadBatch.data.leads.map((l) => (
                            <tr key={l.id}>
                              <td>{l.full_name ?? l.email}</td>
                              <td className="dim">{l.title ?? "—"}</td>
                              <td>{l.company ?? "—"}<div className="faint" style={{ fontSize: 12 }}>{l.industry ?? ""}</div></td>
                              <td className="dim">{l.location ?? "—"}</td>
                              <td>
                                {l.needs_review ? <Chip tone="amber">review</Chip> : null}
                                {l.dedupe_status && l.dedupe_status !== "new" ? <Chip>{l.dedupe_status}</Chip> : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="tag" style={{ marginTop: 8 }}>
                      approve = promote all to contacts + enroll in the James cold sequence · reject = discard the batch
                    </div>
                  </>
                ) : <Empty>loading staged leads…</Empty>
              ) : contentPost ? (
                <>
                  <div className="tag" style={{ marginBottom: 8 }}>
                    content · {contentPost.assetType} · <Chip tone="blue">{contentPost.channel}</Chip>
                    {contentPost.productionCostUsd != null ? (
                      <Chip>
                        {`${contentPost.productionModel ? contentPost.productionModel + " · " : ""}$${contentPost.productionCostUsd < 0.1 ? contentPost.productionCostUsd.toFixed(4) : contentPost.productionCostUsd.toFixed(2)} to generate`}
                      </Chip>
                    ) : null}
                    {contentPost.committeeScore != null ? (
                      <Chip tone={contentPost.committeeOutcome?.startsWith("approved") ? "green" : "amber"}>
                        committee {Math.round(contentPost.committeeScore)}/100 · {contentPost.committeeOutcome}
                      </Chip>
                    ) : null}
                    {contentPost.revisedByCommittee ? <Chip tone="blue">✎ committee-revised</Chip> : null}
                    {contentPost.revisedByFounder ? <Chip tone="green">✎ regenerated from your feedback</Chip> : null}
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = ""; // let the same file be picked again after a failure
                      if (f && open) void uploadImage(open, f);
                    }}
                  />
                  {contentPost.mediaUrl ? (
                    <div style={{ marginBottom: 8 }}>
                      <img src={contentPost.mediaUrl} alt="" style={{ width: "100%", borderRadius: 8, display: "block", border: "1px solid var(--line)" }} />
                      {open.status === "pending" ? (
                        <div className="row" style={{ marginTop: 6 }}>
                          <button className="btn sm" disabled={busy} onClick={() => void regenerateImage(open)}>{busy ? "…" : "Regenerate image"}</button>
                          <button className="btn sm" disabled={busy} onClick={() => fileRef.current?.click()}>Upload replacement</button>
                          <span className="dim" style={{ fontSize: 12 }}>AI draft, or upload your own (PNG/JPEG/WebP, ≤10MB)</span>
                        </div>
                      ) : null}
                    </div>
                  ) : open.status === "pending" && /linkedin|email/i.test(contentPost.channel) ? (
                    <div className="tag" style={{ marginBottom: 8, color: "var(--amber)" }}>
                      no image yet
                      <button className="btn sm" style={{ marginLeft: 8 }} disabled={busy} onClick={() => void regenerateImage(open)}>{busy ? "…" : "Generate image"}</button>
                      <button className="btn sm" style={{ marginLeft: 6 }} disabled={busy} onClick={() => fileRef.current?.click()}>Upload your own</button>
                    </div>
                  ) : null}
                  {contentPost.escalationReason ? (
                    <div className="tag" style={{ color: "var(--amber)", marginBottom: 6 }}>escalated: {contentPost.escalationReason}</div>
                  ) : null}
                  {(contentPost.qaFlags ?? []).length > 0 ? (
                    <div className="tag" style={{ color: "var(--amber)", marginBottom: 6 }}>{(contentPost.qaFlags ?? []).join(" · ")}</div>
                  ) : null}
                  {open.status === "pending" ? (
                    <textarea
                      style={{ width: "100%", minHeight: 240 }}
                      value={edits[`${open.id}:body`] ?? contentPost.body}
                      onChange={(e) => setEdits({ ...edits, [`${open.id}:body`]: e.target.value })}
                    />
                  ) : (
                    <pre className="mono" style={{ fontSize: 14, whiteSpace: "pre-wrap", maxHeight: 280, overflow: "auto" }}>
                      {contentPost.body}
                    </pre>
                  )}
                  {(contentPost.committeeChanges ?? []).length > 0 ? (
                    <>
                      <div className="tag" style={{ marginTop: 6 }}>
                        {contentPost.revisedByCommittee ? "committee changes — applied to the draft above" : "committee notes"}
                      </div>
                      <ul style={{ fontSize: 14, margin: "4px 0 0 16px" }}>
                        {(contentPost.committeeChanges ?? []).map((ch, i) => <li key={i} className="dim">{ch}</li>)}
                      </ul>
                    </>
                  ) : null}
                  {contentPost.revisedByCommittee && contentPost.originalBody ? (
                    <details style={{ marginTop: 8 }}>
                      <summary className="dim" style={{ cursor: "pointer", fontSize: 13 }}>view original draft (before committee revision)</summary>
                      <pre className="mono" style={{ fontSize: 13, whiteSpace: "pre-wrap", maxHeight: 240, overflow: "auto", marginTop: 6, opacity: 0.8 }}>
                        {contentPost.originalBody}
                      </pre>
                    </details>
                  ) : null}
                  {(contentPost.founderFeedback ?? []).length > 0 ? (
                    <>
                      <div className="tag" style={{ marginTop: 8 }}>your feedback, applied in order</div>
                      <ol style={{ fontSize: 14, margin: "4px 0 0 16px" }}>
                        {(contentPost.founderFeedback ?? []).map((f, i) => <li key={i} className="dim">{f}</li>)}
                      </ol>
                    </>
                  ) : null}
                  {open.status === "pending" ? (
                    <>
                      <hr className="rule" />
                      <div className="tag">regenerate from your feedback — the system rewrites the draft above; it stays here for you to review</div>
                      <textarea
                        style={{ width: "100%", minHeight: 64, marginTop: 4 }}
                        placeholder="e.g. open with the bottleneck pain, drop the second paragraph, make the CTA softer…"
                        value={feedback[open.id] ?? ""}
                        onChange={(e) => setFeedback({ ...feedback, [open.id]: e.target.value })}
                      />
                      <div className="row" style={{ marginTop: 6 }}>
                        <button className="btn" disabled={busy || !(feedback[open.id] ?? "").trim()} onClick={() => void regenerate(open)}>
                          {busy ? "Regenerating…" : "Regenerate with feedback"}
                        </button>
                        <span className="dim" style={{ fontSize: 12 }}>honesty-guarded · won't add claims the app can't back</span>
                      </div>
                      <div className="tag" style={{ marginTop: 8 }}>
                        approve = lands in the Studio posting queue (you post it manually) · reject = archived
                      </div>
                    </>
                  ) : null}
                </>
              ) : stateFlag ? (
                <>
                  <div className="tag" style={{ marginBottom: 8 }}>
                    feature switch · <span className="mono">{stateFlag.key}</span> → <Chip tone={stateFlag.value ? "green" : "amber"}>{String(stateFlag.value)}</Chip>
                  </div>
                  {stateFlag.preview?.title ? <div style={{ marginBottom: 6, fontWeight: 600 }}>{stateFlag.preview.title}</div> : null}
                  {stateFlag.preview?.what ? <div className="dim" style={{ marginBottom: 10, fontSize: 14 }}>{stateFlag.preview.what}</div> : null}
                  {stateFlag.preview?.subject ? <div className="tag">subject: {stateFlag.preview.subject}</div> : null}
                  {stateFlag.preview?.body ? (
                    <pre className="mono" style={{ fontSize: 14, whiteSpace: "pre-wrap", maxHeight: 300, overflow: "auto" }}>
                      {stateFlag.preview.body}
                    </pre>
                  ) : null}
                  {open.status === "pending" ? (
                    <div className="tag" style={{ marginTop: 8 }}>
                      approve = the switch flips and the feature goes live · reject = stays off
                    </div>
                  ) : null}
                </>
              ) : replyDraft ? (
                <>
                  <div className="tag" style={{ marginBottom: 8 }}>
                    reply draft · to {replyDraft.contactName ? `${replyDraft.contactName} <${replyDraft.contactEmail}>` : replyDraft.contactEmail}
                    {" "}· <Chip tone={replyDraft.intent === "qualified" ? "amber" : undefined}>{replyDraft.intent}</Chip>
                    {replyDraft.calendarIncluded ? <Chip>calendar link</Chip> : null}
                  </div>
                  {replyDraft.summary ? (
                    <div className="dim" style={{ marginBottom: 10, fontSize: 14 }}>their reply: {replyDraft.summary}</div>
                  ) : null}
                  {open.status === "pending" ? (
                    <>
                      <div className="tag">subject</div>
                      <input
                        style={{ width: "100%", marginBottom: 10 }}
                        value={edits[`${open.id}:subject`] ?? replyDraft.draftSubject}
                        onChange={(e) => setEdits({ ...edits, [`${open.id}:subject`]: e.target.value })}
                      />
                      <div className="tag">body (sign-off and AI disclosure footer are added automatically)</div>
                      <textarea
                        style={{ width: "100%", minHeight: 220 }}
                        value={edits[`${open.id}:body`] ?? replyDraft.draftBodyText}
                        onChange={(e) => setEdits({ ...edits, [`${open.id}:body`]: e.target.value })}
                      />
                    </>
                  ) : (
                    <>
                      <div className="tag">{replyDraft.draftSubject}</div>
                      <pre className="mono" style={{ fontSize: 14, whiteSpace: "pre-wrap", maxHeight: 280, overflow: "auto" }}>
                        {replyDraft.draftBodyText}
                      </pre>
                    </>
                  )}
                  {open.status === "pending" ? (
                    <div className="tag" style={{ marginTop: 8 }}>
                      approve = the email sends immediately as James · reject = nothing is sent
                    </div>
                  ) : null}
                </>
              ) : changes.length > 0 ? (
                changes.map((c, i) => (
                  <div key={i} style={{ marginBottom: 14 }}>
                    <div className="tag">step {c.stepNumber} · {c.field}</div>
                    <div style={{ margin: "6px 0" }}><span className="diff-before">{c.before}</span></div>
                    {open.status === "pending" ? (
                      <textarea
                        style={{ width: "100%", minHeight: 64 }}
                        value={edits[`${open.id}:${i}`] ?? c.after}
                        onChange={(e) => setEdits({ ...edits, [`${open.id}:${i}`]: e.target.value })}
                      />
                    ) : (
                      <div><span className="diff-after">{c.after}</span></div>
                    )}
                  </div>
                ))
              ) : (
                <pre className="mono" style={{ fontSize: 13, whiteSpace: "pre-wrap", maxHeight: 300, overflow: "auto" }}>
                  {JSON.stringify(open.payload, null, 2)}
                </pre>
              )}

              {open.status === "pending" ? (
                <>
                  <hr className="rule" />
                  <div className="row">
                    <button className="btn ghost-green" disabled={busy} onClick={() => decide(open, "approved")}>Approve</button>
                    {changes.length > 0 || replyDraft || contentPost ? (
                      <button className="btn" disabled={busy} onClick={() => decide(open, "edited")}>Approve with edits</button>
                    ) : null}
                    <span className="spacer" />
                    <select value={reason} onChange={(e) => setReason(e.target.value)}>
                      {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <button className="btn danger" disabled={busy} onClick={() => decide(open, "rejected")}>Reject</button>
                  </div>
                </>
              ) : (
                <div className="tag" style={{ marginTop: 8 }}>
                  decided {fmtDateTime(open.decided_at)} {open.decision_reason ? `· reason: ${open.decision_reason}` : ""}
                </div>
              )}
            </>
          )}
        </Panel>
      </div>
    </>
  );
}
